import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";

import "./IndivididualConfidenceModal.scss";
import { Dialog } from "../../../../../components/dialog/Dialog";
import { NumberSelection } from "../../../../../components/number-selection/NumberSelection";
import { translationService } from "../../../../../services/translation/TranslationService";
import {
  DataSection,
  SubSection,
} from "../../../../../components/data-components/sections/DataSections";
import { SegmentationService } from "../../../../../services/segmentation/SegmentationService";
import { AnalysisConfidenceStorage } from "../../../../../services/organization-analysis-confidence/AnalysisConfidenceStorage";
import { CodeDetection, ConfidenceLevels } from "../../../../../api-models/response/VideoAnalysisDataResponse";

export default observer(function DetectionIndivididualConfidenceModal(props: {analysisConfidenceStorage: AnalysisConfidenceStorage, videoSegmentation: SegmentationService}) {
  const [confidenceLevels, setConfidenceLevels] = useState<ConfidenceLevels>({});
  const {videoSegmentation, analysisConfidenceStorage} = {...props};

  useEffect(() => {
    async function getConfidenceLevels() {
      const confidenceLevels: ConfidenceLevels = {};

      if (analysisConfidenceStorage.HighlightLevels.length === 0) {
        await analysisConfidenceStorage.load();
      }

      analysisConfidenceStorage.HighlightLevels.forEach((code) => {
        confidenceLevels[code.name] = code.level * 100;
      });

      setConfidenceLevels(confidenceLevels);
    }

    getConfidenceLevels();
  }, [analysisConfidenceStorage]);

  const confirmIndividualConfidence = () => {
    const processedConfidenceLevels: ConfidenceLevels = {};

    videoSegmentation.detectedGroupCodes.forEach((code) => {
      processedConfidenceLevels[code.code] = confidenceLevels[code.code]/100;
    });

    videoSegmentation.setIndividualDetectionConfidence(processedConfidenceLevels);
    videoSegmentation.hideDetectionIndivididualConfidenceModal();
  };

  const getNumberSelection = (code: CodeDetection) => {
    const translatedDetection = translationService.translate(
      `damages.highlight-detection.${code.code.replace(/\s+/g, "-").toLowerCase()}`
    );

    return (
      <NumberSelection
        key={code.code}
        initialValue={confidenceLevels[code.code]}
        minValue={65}
        maxValue={100}
        step={5}
        labelTranslationKey={translationService.translate("general.add-percentage-at-end", {
          text: translatedDetection,
        })}
        callback={(newValue) => {
          setConfidenceLevels((prev) => {
            return {
              ...prev,
              [code.code]: newValue,
            };
          });
        }}
      />
    );
  };

  return (
    <Dialog
      sidePosition
      isVisible={videoSegmentation.shouldShowDetectionIndivididualConfidenceModal}
      onHide={videoSegmentation.hideDetectionIndivididualConfidenceModal}
      titleKey={"analysis.detection-individual-confidence.title"}
      subTextKey={"analysis.detection-individual-confidence.subtitle"}
      buttonOk={{
        textKey: "general.apply",
        action: confirmIndividualConfidence,
      }}
      buttonCancel={{
        textKey: "general.cancel",
        action: () => {
          videoSegmentation.hideDetectionIndivididualConfidenceModal();
        },
      }}
    >
      <DataSection>
        <SubSection>
          {videoSegmentation.detectedGroupCodes
            .slice(undefined, videoSegmentation.detectedGroupCodes.length / 2 + 1)
            .map((code) => getNumberSelection(code))}
        </SubSection>
        <SubSection>
          {videoSegmentation.detectedGroupCodes
            .slice(videoSegmentation.detectedGroupCodes.length / 2 + 1)
            .map((code) => getNumberSelection(code))}
        </SubSection>
      </DataSection>
    </Dialog>
  );
});
